import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useGlobalLoader } from './useGlobalLoader';

/**
 * Custom hook for managing page-specific loading states
 * Prevents showing loaders when switching between tabs on the same page
 * Only shows loader for initial page loads or when data is stale
 */
export const usePageLoader = (options = {}) => {
  const {
    initialLoading = false,
    staleTime = 5 * 60 * 1000, // 5 minutes default
    enableCache = true,
    loadingText = "Loading...",
  } = options;

  const [isLoading, setIsLoading] = useState(initialLoading);
  const [error, setError] = useState(null);
  const location = useLocation();
  const lastLoadTimeRef = useRef(null);
  const isInitialMountRef = useRef(true);
  const { showLoader, hideLoader } = useGlobalLoader();

  // Check if data is stale
  const isDataStale = () => {
    if (!enableCache || !lastLoadTimeRef.current) return true;
    return Date.now() - lastLoadTimeRef.current > staleTime;
  };

  // Check if this is a fresh page load
  const isFreshPageLoad = () => {
    return isInitialMountRef.current;
  };

  // Show loader only if it's a fresh page load or data is stale
  const shouldShowLoader = () => {
    return isFreshPageLoad() || isDataStale();
  };

  // Start loading
  const startLoading = () => {
    if (shouldShowLoader()) {
      setIsLoading(true);
      setError(null);
      showLoader(loadingText);
    }
  };

  // Stop loading
  const stopLoading = () => {
    setIsLoading(false);
    lastLoadTimeRef.current = Date.now();
    isInitialMountRef.current = false;
    hideLoader();
  };

  // Set error
  const setLoadingError = (error) => {
    setError(error);
    setIsLoading(false);
    hideLoader();
  };

  // Reset loading state (useful for retries)
  const resetLoading = () => {
    setIsLoading(false);
    setError(null);
    lastLoadTimeRef.current = null;
    isInitialMountRef.current = true;
    hideLoader();
  };

  // Clear cache when location changes
  useEffect(() => {
    isInitialMountRef.current = true;
    lastLoadTimeRef.current = null;
  }, [location.pathname]);

  return {
    isLoading,
    error,
    startLoading,
    stopLoading,
    setLoadingError,
    resetLoading,
    shouldShowLoader,
    isDataStale,
  };
};

/**
 * Hook for managing async operations with intelligent loading
 */
export const useAsyncOperation = (asyncFunction, options = {}) => {
  const {
    immediate = true,
    dependencies = [],
    ...loaderOptions
  } = options;

  const loader = usePageLoader(loaderOptions);
  const [data, setData] = useState(null);

  const execute = async (...args) => {
    try {
      loader.startLoading();
      const result = await asyncFunction(...args);
      setData(result);
      loader.stopLoading();
      return result;
    } catch (error) {
      loader.setLoadingError(error);
      throw error;
    }
  };

  useEffect(() => {
    if (immediate) {
      execute();
    }
  }, dependencies);

  return {
    ...loader,
    data,
    execute,
  };
};
